import apiRequest from './api-request';

export type AdminOrderFile = {
    id: number;
    groupId: string;
    version: number;
    originalFilename: string;
    mimeType: string;
    byteSize: number;
    title: string;
    description: string;
    createdAt: string;
    deletedAt: string | null;
    uploadedByTenantId: number;
    uploadedByTenantName: string;
    uploadedByUserName: string | null;
};

export function listAdminOrderFiles(orderId: number | string) {
    return apiRequest({
        method: 'GET',
        resource: `orders/${orderId}/files`
    }) as Promise<AdminOrderFile[]>;
}

export function downloadAdminOrderFile(orderId: number | string, fileId: number) {
    return apiRequest({
        method: 'POST',
        resource: `orders/${orderId}/files/${fileId}/download`
    }) as Promise<{ presignedUrl: string }>;
}

export function restoreAdminOrderFileGroup(orderId: number | string, groupId: string) {
    return apiRequest({
        method: 'POST',
        resource: `orders/${orderId}/files/${groupId}/restore`
    });
}

export function hardDeleteAdminOrderFileGroup(orderId: number | string, groupId: string) {
    return apiRequest({
        method: 'POST',
        resource: `orders/${orderId}/files/${groupId}/delete`
    });
}

const MAX_ORDER_FILE_BYTES = 25 * 1024 * 1024;

const ALLOWED_ORDER_FILE_MIME_TYPES = new Set([
    'application/pdf',
    'image/png',
    'image/jpeg',
    'image/jpg',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/vnd.ms-excel',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
]);

const EXTENSION_MIME_TYPES: Record<string, string> = {
    pdf: 'application/pdf',
    png: 'image/png',
    jpg: 'image/jpeg',
    jpeg: 'image/jpeg',
    doc: 'application/msword',
    docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    xls: 'application/vnd.ms-excel',
    xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
};

export const ADMIN_ORDER_FILE_ACCEPT = '.pdf,.png,.jpg,.jpeg,.doc,.docx,.xls,.xlsx';

function fileExtension(name: string) {
    const base = name.split(/[/\\]/).pop() ?? '';
    const dot = base.lastIndexOf('.');
    return dot < 0 ? '' : base.slice(dot + 1).toLowerCase();
}

export function adminOrderFileMimeType(file: File) {
    const type = file.type.toLowerCase().split(';')[0].trim();
    if (ALLOWED_ORDER_FILE_MIME_TYPES.has(type)) {
        return type;
    }
    const fromExtension = EXTENSION_MIME_TYPES[fileExtension(file.name)];
    if (fromExtension && (!type || type === 'application/octet-stream' || type === 'image/pjpeg')) {
        return fromExtension;
    }
    return null;
}

export function adminOrderFileClientError(file: File) {
    if (!adminOrderFileMimeType(file)) {
        return 'Filtypen stöds inte. Välj PDF, PNG, JPG, JPEG eller Office (doc, docx, xls, xlsx).';
    }
    if (file.size > MAX_ORDER_FILE_BYTES) {
        return 'Filen får vara högst 25 MB.';
    }
    if (file.size <= 0) {
        return 'Filen är tom.';
    }
    return null;
}

export function presignAdminOrderFile(orderId: number | string, groupId: string, body: {
    mimeType: string;
    originalFilename: string;
    title: string;
    description?: string;
}) {
    return apiRequest({
        method: 'POST',
        resource: `orders/${orderId}/files/${groupId}/presign`,
        body
    }) as Promise<{ presignedUrl: string; s3Key: string }>;
}

export function confirmAdminOrderFile(orderId: number | string, groupId: string, body: {
    mimeType: string;
    originalFilename: string;
    title: string;
    description?: string;
    s3Key: string;
}) {
    return apiRequest({
        method: 'POST',
        resource: `orders/${orderId}/files/${groupId}/confirm`,
        body
    });
}
